import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { ConfirmDialog } from "primereact/confirmdialog";
import { InputTextarea } from "primereact/inputtextarea";
import { Toast } from 'primereact/toast';
import { classNames } from "primereact/utils";
import React, { useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { CarePlanSectionProps, NA, RESIDENT_CARE_PLAN_TYPE } from "../../../constants/constants";
import { ASSISTANCE_REQUIRED_MESSAGE } from "../../../constants/carePlanConstants";
import SectionGenieDialog from "./SectionGenieDialog";
import {
  LEFT_ALONE_GENIE_FIELD_DEFINITIONS,
  LEFT_ALONE_GENIE_QUESTIONS,
  LeftAloneGenieSectionValues,
} from "./leftAloneGenie";
import { useCarePlanAutoSave } from "./useCarePlanAutoSave";

const LeftAloneSection: React.FC<CarePlanSectionProps> = ({
  sectionData, submitButtonClick, backButtonClick, nextButtonClick, showHelpMessage, loadingCarePlan
}) => {
  const [enableEditing, setEnableEditing] = useState<boolean>(false);
  const [showGenieDialog, setShowGenieDialog] = useState<boolean>(false);

  const toast = useRef<Toast>(null);

  const defaultValues = {
    carePlanType: RESIDENT_CARE_PLAN_TYPE.LEFT_ALONE_STATUS,

    leftAloneDetails: sectionData?.leftAloneDetails || '',
    leftAloneAssistanceDetails: sectionData?.leftAloneAssistanceDetails || '',
  }

  const { control, formState: { errors }, handleSubmit, reset, watch, register, getValues, setValue } = useForm({ defaultValues });

  const getFormErrorMessage = (name) => {
    return errors[name] && <small className="p-error">{errors[name].message}</small>
  };

  const onsubmit = async (data: any) => {
    submitButtonClick && submitButtonClick(data);
  }

  const onManualSave = async (data: any) => {
    submitButtonClick && submitButtonClick(data, { showSuccessToast: true });
  }

  useCarePlanAutoSave({
    enabled: enableEditing,
    baselineData: defaultValues,
    watch,
    handleSubmit,
    onSubmit: onsubmit,
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1em" }} className="m-2 p-component resident-careplan-section">
      <Toast ref={toast}></Toast>
      <SectionGenieDialog
        visible={showGenieDialog}
        title="Using Genie now"
        questions={LEFT_ALONE_GENIE_QUESTIONS}
        fieldDefinitions={LEFT_ALONE_GENIE_FIELD_DEFINITIONS}
        currentValues={getValues() as LeftAloneGenieSectionValues}
        successMessage="Left alone care plan updated."
        errorMessage="Unable to update the left alone care plan."
        onHide={() => setShowGenieDialog(false)}
        onApply={async (payload) => {
          const nextValues = { ...getValues(), ...payload.valueUpdates };
          Object.entries(payload.valueUpdates).forEach(([fieldKey, fieldValue]) => {
            setValue(fieldKey as keyof typeof defaultValues, fieldValue as never, { shouldDirty: true });
          });
          await onsubmit(nextValues);
        }}
      />

      <Card
        className="resident-careplan-card"
        subTitle="Document the ability for resident to be left unattended for a specific length of time. WAC 388-76-0355(9)"
        footer={
          <div className="flex justify-content-center gap-2">
            <Button label="Prev" icon="pi pi-arrow-left" severity="info" outlined rounded iconPos="left" onClick={backButtonClick} />
            <Button
              label="Use Genie's Help to Fill"
              icon="pi pi-sparkles"
              severity="warning"
              rounded
              onClick={() => {
                setEnableEditing(true);
                setShowGenieDialog(true);
              }}
              visible={enableEditing}
            />
            <Button label="Edit" icon="pi pi-pencil" severity="warning" outlined rounded onClick={() => setEnableEditing(true)} visible={!enableEditing} />
            <Button label="Disable Edit" icon="pi pi-lock" visible={enableEditing} severity="secondary" outlined rounded onClick={() => { setEnableEditing(false); reset(defaultValues); }} />
        <Button label="Save" loading={loadingCarePlan} disabled={!enableEditing} severity="warning" outlined rounded onClick={handleSubmit(onManualSave)} visible={enableEditing} />
            <Button label="Next" icon="pi pi-arrow-right" severity="info" outlined rounded iconPos="right"
              onClick={nextButtonClick} />
          </div>
        } >
        <div className="">
          <form className="">
            <div className="flex-column flex-wrap ">
              {
                enableEditing ? (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <span className="flex align-items-center gap-2 mb-2">
                        <label htmlFor="leftAloneDetails" className={classNames({ 'p-error': errors.leftAloneDetails })}>
                          Add left alone details:
                        </label>
                      </span>
                      <span className="p-float-label">
                        <Controller name="leftAloneDetails" control={control}
                          rules={{ required: 'Left Alone details is required.' }}
                          render={({ field, fieldState }) => (
                            <InputTextarea placeholder="Add Left Alone details"
                              style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                          )} />
                      </span>
                      {getFormErrorMessage('leftAloneDetails')}
                    </div>

                    <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <span className="flex align-items-center gap-2 mb-2">
                        <label htmlFor="leftAloneAssistanceDetails" className={classNames({ 'p-error': errors.leftAloneAssistanceDetails })}>
                          {ASSISTANCE_REQUIRED_MESSAGE}
                        </label>
                      </span>
                      <span className="p-float-label">
                        <Controller name="leftAloneAssistanceDetails" control={control}
                          render={({ field, fieldState }) => (
                            <InputTextarea placeholder="Describe any assistance required for left alone status"
                              style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                          )} />
                      </span>
                      {getFormErrorMessage('leftAloneAssistanceDetails')}
                    </div>
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}>
                      <strong>Left alone details: </strong>
                      <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.leftAloneDetails || NA)}</span>
                    </div>
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                      <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.leftAloneAssistanceDetails || NA)}</span>
                    </div>
                  </div>
                )
              }
            </div>
          </form>
        </div>
      </Card >
    </div >
  );
};

export default LeftAloneSection;


